import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TableCell,
  TableRow,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../config/api';
import DataTable from '../components/DataTable';

// end date wise show satus expried in how many days
const getStatus = (valid_until) => {
  const now = new Date();
  const endDate = new Date(valid_until);
  const diffTime = Math.abs(endDate - now);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  if (diffDays > 0) { return `${diffDays} days left`; } else { return 'Expired'; }
};
function PromoCodes() {
  const [promoCodes, setPromoCodes] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: '',
    max_uses: 1,
    trainer_id: '',
    valid_from: '',
    valid_until: '',
    status: 'active',
  });
  const [error, setError] = useState('');

  useEffect(() => {
    fetchPromoCodes();
    fetchTrainers();
  }, []);

  const fetchPromoCodes = async () => {
    try {
      setLoading(true);
      const response = await api.get('/promo-codes');
      setPromoCodes(response.data.data || []);
    } catch (error) {
      console.error('Error fetching promo codes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrainers = async () => {
    try {
      const response = await api.get('/trainers');
      setTrainers(response.data.data || []);
    } catch (error) {
      console.error('Error fetching trainers:', error);
    }
  };

  const handleOpen = (promoCode = null) => {
    if (promoCode) {
      setEditing(promoCode);
      setFormData({
        ...promoCode,
        valid_from: promoCode.valid_from ? new Date(promoCode.valid_from).toISOString().split('T')[0] : new Date().toISOString().split('T')[0] + 'T00:00:00',
        valid_until: promoCode.valid_until ? new Date(promoCode.valid_until).toISOString().split('T')[0] : new Date().toISOString().split('T')[0] + 'T23:59:59',
        trainer_id: promoCode.trainer_id || '',
      });
    } else {
      setEditing(null);
      setFormData({
        code: '',
        description: '',
        discount_type: 'percentage',
        discount_value: '',
        max_uses: 1,
        trainer_id: '',
        valid_from: '',
        valid_until: '',
        status: 'active',
      });
    }
    setOpen(true);
    setError('');
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
    setError('');
  };

  const handleSubmit = async () => {
    try {
      setError('');
      const submitData = {
        ...formData,
        trainer_id: formData.trainer_id || null,
        valid_from: formData.valid_from || null,
        valid_until: formData.valid_until || null,
      };
      if (editing) {
        await api.put(`/promo-codes/${editing.id}`, submitData);
      } else {
        await api.post('/promo-codes', submitData);
      }
      handleClose();
      fetchPromoCodes();
    } catch (error) {
      setError(error.response?.data?.error || 'Error saving promo code');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this promo code?')) {
      try {
        await api.delete(`/promo-codes/${id}`);
        fetchPromoCodes();
      } catch (error) {
        console.error('Error deleting promo code:', error);
      }
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Promo Codes</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          Add Promo Code
        </Button>
      </Box>

      <DataTable
        data={promoCodes}
        loading={loading}
        searchFields={['code', 'description']}
        emptyMessage="No promo codes found"
        columns={[
          { field: 'id', label: 'ID' },
          { field: 'code', label: 'Code' },
          { field: 'discount_type', label: 'Discount Type' },
          { field: 'discount_value', label: 'Discount Value' },
          { field: 'max_uses', label: 'Max Uses' },
          { field: 'used_count', label: 'Used Count' },
          { field: 'trainer_name', label: 'Trainer' },
          { field: 'valid_from', label: 'Valid On' },
          { field: 'valid_until', label: 'Valid Until' },
          { field: 'status', label: 'Valid till' },
          { field: 'actions', label: 'Actions' },
        ]}
        renderRow={(promoCode) => (
          <TableRow key={promoCode.id}>
            <TableCell>{promoCode.id}</TableCell>
            <TableCell>{promoCode.code}</TableCell>
            <TableCell>{promoCode.discount_type}</TableCell>
            <TableCell>{promoCode.discount_value}</TableCell>
            <TableCell>{promoCode.max_uses}</TableCell>
            <TableCell>{promoCode.used_count || 0}</TableCell>
            <TableCell>{promoCode.trainer_name || 'All'}</TableCell>
            <TableCell>{getStatus(promoCode.valid_until)}</TableCell>
            <TableCell>{promoCode.valid_from ? new Date(promoCode.valid_from).toLocaleDateString() : ''}</TableCell>
            <TableCell>{promoCode.valid_until ? new Date(promoCode.valid_until).toLocaleDateString() : ''}</TableCell>
            <TableCell>
              <IconButton size="small" onClick={() => handleOpen(promoCode)}>
                <EditIcon />
              </IconButton>
              <IconButton size="small" onClick={() => handleDelete(promoCode.id)}>
                <DeleteIcon />
              </IconButton>
            </TableCell>
          </TableRow>
        )}
      />

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit Promo Code' : 'Add Promo Code'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            fullWidth
            label="Code"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
            multiline
            rows={2}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Discount Type</InputLabel>
            <Select
              value={formData.discount_type}
              onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
              label="Discount Type"
            >
              <MenuItem value="percentage">Percentage</MenuItem>
              <MenuItem value="fixed">Fixed</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Discount Value"
            type="number"
            value={formData.discount_value}
            onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Max Uses"
            type="number"
            value={formData.max_uses}
            onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
            margin="normal"
            onWheel={(e) => e.target.blur()}
          />
          {/* <FormControl fullWidth margin="normal" hiddenLabel>
            <InputLabel>Trainer (Optional)</InputLabel>
            <Select
              value={formData.trainer_id}
              onChange={(e) => setFormData({ ...formData, trainer_id: e.target.value })}
              label="Trainer (Optional)"
            >
              <MenuItem value="">All Trainers</MenuItem>
              {trainers.map((trainer) => (
                <MenuItem key={trainer.id} value={trainer.id}>
                  {trainer.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl> */}
          <TextField
            fullWidth
            label="Valid From"
            type="date"
            value={formData.valid_from}
            onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            fullWidth
            label="Valid Until"
            type="date"
            value={formData.valid_until}
            onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              label="Status"
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PromoCodes;

